import React, { useEffect, useState } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import ExerciseTargetTable from '../../../components/tables/ExerciseTargetTable';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import { useExerciseTargetApi } from '../../../hooks/useExerciseTargetApi';
import AddNewButton from '../../../components/common/AddButton';
import { confirmDelete } from '../../../utils/sweetAlertUtils';
import { ExerciseTargetDTO } from '../../../services/apiClient';
import { useNavigate } from 'react-router-dom';

const ExerciseTargetList: React.FC = () => {
  const { search, remove, isLoading, error } = useExerciseTargetApi();
  const [items, setItems] = useState<ExerciseTargetDTO[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchFilters, setSearchFilters] = useState<any>({});

  const navigate = useNavigate();
  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'ExerciseTargets', href: '#' },
  ];

  const fetchItems = async (page: number, size: number, filters: any) => {
    try {
      const response = await search({
        filters,
        page,
        pageSize: size,
        sortBy: 'Code',
        sortDescending: false,
      });
      setItems(response?.data?.results || []);
      setTotalRecords(response?.data?.totalCount || 0);
    } catch (error) {
      console.error('Error fetching ExerciseTargets:', error);
    }
  };

  useEffect(() => {
    fetchItems(currentPage, pageSize, {});
  }, [currentPage, pageSize]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchItems(1, pageSize, searchFilters);
  };

  const handleClearSearch = () => {
    setSearchFilters({});
    fetchItems(1, pageSize, {});
  };

  const handleDelete = async (dto: ExerciseTargetDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    try {
      await remove(dto.id || '');
      await fetchItems(currentPage, pageSize, {});
    } catch (error) {
      console.error('Error deleting ExerciseTarget:', error);
    }
  };

  return (
    <AdminLayout
      activeMenuItem="/admin/exercisetargets"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
      requiredPermission='Settings.ExerciseTarget.View'
    >
      <Card
        title="Manage Exercise Targets"
        subtitle={`Total Records: ${totalRecords}`}
        headerActions={
          <AddNewButton onClick={() => navigate('/admin/exercisetargets/create')} />
        }
      >
        <form onSubmit={handleSearch} className="mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <FormField
              id="title"
              label="Search by Title"
              value={searchFilters.title || ''}
              onChange={(e) => setSearchFilters({ ...searchFilters, title: e.target.value })}
              placeholder="Enter Title"
            />
            <FormField
              id="code"
              label="Search by Code"
              value={searchFilters.code || ''}
              onChange={(e) => setSearchFilters({ ...searchFilters, code: e.target.value })}
              placeholder="Enter Code"
            />
            <FormField
              id="description"
              label="Search by Description"
              value={searchFilters.description || ''}
              onChange={(e) => setSearchFilters({ ...searchFilters, description: e.target.value })}
              placeholder="Enter Description"
            />
          </div>

          <div className="flex space-x-4 mb-4">
            <Button variant="secondary" onClick={handleClearSearch}>
              Clear Search
            </Button>
            <Button type="submit">Search</Button>
          </div>
        </form>

        <ExerciseTargetTable
          data={items}
          handleEdit={(dto) =>
            navigate(`/admin/exercisetargets/${dto.id}`)
          }
          handleDelete={handleDelete}
          currentPage={currentPage}
          totalPages={Math.ceil(totalRecords / pageSize)}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={isLoading}
        />
      </Card>
    </AdminLayout>
  );
};

export default ExerciseTargetList;